import { useState } from 'react';

import { SoftCard } from '@/components/ui/soft-card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import myShopClient from '@services/nearlive';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { useChannels } from '@/hooks/useChannels';
import { useInvoices } from '@/hooks/useInvoices';
import { useLocalAds } from '@/hooks/useInsights';
import { useShop } from '@/hooks/useShop';
import { useSubscribe } from '@/hooks/useSubscribe';
import { SettingsHeader } from './components/SettingsHeader';
import { AddOnsSection } from './components/billing/AddOnsSection';
import { BillingAddressCard } from './components/billing/BillingAddressCard';
import { CancelSubscriptionFlow } from './components/billing/CancelSubscriptionFlow';
import { InvoicesTable } from './components/billing/InvoicesTable';
import { MigrationInfoCard } from './components/billing/MigrationInfoCard';
import { PaymentMethodCard } from './components/billing/PaymentMethodCard';
import { PlanCard } from './components/billing/PlanCard';
import { PlanChangeSheet } from './components/billing/PlanChangeSheet';
import { TrialStatusBanner } from './components/billing/TrialStatusBanner';
import { UpdateCardSheet } from './components/billing/UpdateCardSheet';
import { UpgradeSuccessAlert } from './components/billing/UpgradeSuccessAlert';
import { formatCurrency } from '@/lib/currency';

export function formatAmount(amount, currency) {
	return amount == null ? null : formatCurrency(amount, currency);
}

/**
 * Billing & plan page.
 *
 * Source of truth: `useBillingStatus` (NEA-5710). Every status / plan / trial
 * / payment-method / address surface keys off the live-Stripe read-through.
 *
 * Write paths (NEA-5793, absorbed from NEA-5770):
 *   - Subscribe         → `useSubscribe()` → POST /api/billing/subscription/{shopId} (NEA-5751)
 *   - Add / Update card → POST /api/billing/checkout-session/{shopId} (mode: setup)
 *   - Change plan       → POST /api/billing/plan/{shopId}/{plan} (NEA-5591; Starter → Pro only)
 *   - Save address      → POST /api/billing/address/{shopId}
 *   - Resume sub        → POST /api/billing/subscription/{shopId}/resume (new handler)
 */
export function Billing() {
	const { shop, mutate: mutateShops } = useShop();
	const { billing, isLoading: billingLoading, mutate: mutateBilling } = useBillingStatus(shop?.id);
	const { channels: enabledChannels } = useChannels(shop?.id);
	const { invoices, isLoading: invoicesLoading } = useInvoices(shop?.id);
	const { data: localAdsData } = useLocalAds(shop?.id);
	const { subscribe } = useSubscribe();

	const [tab, setTab] = useState('plan');
	const [cardSheet, setCardSheet] = useState({ open: false, mode: 'update' });
	const [planSheetOpen, setPlanSheetOpen] = useState(false);
	const [pendingAction, setPendingAction] = useState(null);
	const [actionError, setActionError] = useState(null);

	if (!shop || billingLoading) return <BillingSkeleton />;

	const runAction = async (key, fn) => {
		setPendingAction(key);
		setActionError(null);
		try {
			await fn();
		} catch (err) {
			const message =
				err?.response?.data?.error ?? err?.response?.data?.message ?? err?.message ?? 'Something went wrong. Please try again.';
			setActionError(message);
			// Scroll the top-of-page error banner into view for CTAs further down the page.
			if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') window.scrollTo({ top: 0, behavior: 'smooth' });
		} finally {
			setPendingAction(null);
		}
	};

	const openAddCard = () => setCardSheet({ open: true, mode: 'add' });
	const openUpdateCard = () => setCardSheet({ open: true, mode: 'update' });
	const closeCardSheet = () => setCardSheet((s) => ({ ...s, open: false }));

	const startCheckout = () =>
		runAction('checkout', async () => {
			const client = await myShopClient();
			const { data } = await client.post(`/api/billing/card-checkout/${shop.id}`);
			if (!data?.url) throw new Error('Stripe Checkout returned no redirect URL');
			window.location.assign(data.url);
		});

	const handleSubscribe = (plan) =>
		runAction('subscribe', async () => {
			await subscribe(plan);
			setPlanSheetOpen(false);
		});

	// A customer-cancelled sub past period-end is deleted on Stripe — the only
	// way back is a NEW subscription, so the plan sheet must route to subscribe
	// (change-plan would 400 against the dead sub).
	const needsNewSubscription =
		billing?.status === 'no_subscription' || (billing?.expired && billing?.cancellationReason === 'cancellation_requested');

	const handlePlanChange = (nextPlan) =>
		runAction('change-plan', async () => {
			if (needsNewSubscription) {
				await subscribe(nextPlan);
			} else {
				const client = await myShopClient();
				await client.post(`/api/billing/plan/${shop.id}/${nextPlan}`);
				await mutateBilling();
			}
			setPlanSheetOpen(false);
		});

	const handleSaveAddress = (draft) =>
		runAction('address', async () => {
			const client = await myShopClient();
			await client.post(`/api/billing/address/${shop.id}`, draft);
			await mutateBilling();
		});

	const handleResume = () =>
		runAction('resume', async () => {
			const client = await myShopClient();
			await client.post(`/api/billing/subscription/${shop.id}/resume`);
			// A location reactivate clears `closeAt` on the shop row, which rides
			// the shops cache — refresh both so the banner drops immediately.
			await Promise.all([mutateBilling(), mutateShops()]);
		});

	const planAmount = billing?.amount ?? null;
	const planCurrency = billing?.currency ?? 'gbp';
	const planInterval = billing?.interval ?? 'month';
	const localAdsBudget = localAdsData?.budget?.daily ?? null;

	return (
		<div className="max-w-3xl space-y-8">
			<SettingsHeader
				eyebrow="Account"
				title="Billing & plan"
				description="Your active plan, payment method, billing address, and past invoices."
			/>

			<UpgradeSuccessAlert />
			{actionError ? <ErrorAlert message={actionError} onDismiss={() => setActionError(null)} /> : null}

			<SectionTabs value={tab} onChange={setTab} />

			{tab === 'plan' ? (
				<div className="space-y-8">
					<MigrationInfoCard billing={billing} />
					<TrialStatusBanner
						billing={billing}
						onAddCard={needsNewSubscription ? () => setPlanSheetOpen(true) : startCheckout}
						onResubscribe={() => setPlanSheetOpen(true)}
						resumePending={pendingAction === 'resume'}
						onResume={handleResume}
					/>
					<PlanCard
						billing={billing}
						planAmount={planAmount}
						planInterval={planInterval}
						planCurrency={planCurrency}
						timeline={billing?.timeline}
						onChangePlan={() => setPlanSheetOpen(true)}
						onAddCard={needsNewSubscription ? () => setPlanSheetOpen(true) : startCheckout}
					/>
					<AddOnsSection
						billing={billing}
						channels={enabledChannels}
						localAdsBudget={localAdsBudget}
						localAdsCurrency={shop?.currency}
					/>
					{canShowCloseAccount(billing) ? <CancelSubscriptionFlow shopId={shop.id} /> : null}
				</div>
			) : null}

			{tab === 'billing' ? (
				<div className="space-y-8">
					<PaymentMethodCard
						paymentMethod={billing?.paymentMethod}
						hasPaymentMethod={Boolean(billing?.hasPaymentMethod)}
						onUpdate={billing?.hasPaymentMethod ? openUpdateCard : openAddCard}
					/>
					<BillingAddressCard address={billing?.address} email={billing?.email} onSave={handleSaveAddress} />
				</div>
			) : null}

			{tab === 'invoices' ? <InvoicesTable invoices={invoices} isLoading={invoicesLoading} /> : null}

			<UpdateCardSheet
				open={cardSheet.open}
				mode={cardSheet.mode}
				onOpenChange={(open) => setCardSheet((s) => ({ ...s, open }))}
				onConfirm={() => {
					closeCardSheet();
					startCheckout();
				}}
				pending={pendingAction === 'checkout'}
			/>
			<PlanChangeSheet
				open={planSheetOpen}
				onOpenChange={setPlanSheetOpen}
				currentPlan={needsNewSubscription ? null : (billing?.plan ?? null)}
				status={needsNewSubscription ? 'no_subscription' : billing?.status}
				catalogue={billing?.catalogue}
				returning={Boolean(billing?.expired && billing?.cancellationReason === 'cancellation_requested')}
				onConfirm={needsNewSubscription ? handleSubscribe : handlePlanChange}
				pending={pendingAction === 'subscribe' || pendingAction === 'change-plan'}
			/>
		</div>
	);
}

/**
 * The Close-account section only renders when there's an actually cancellable
 * sub — i.e. an active, trialing or past_due Unify sub that isn't already
 * cancelling (the backend accepts cancel on past_due — a card-failed customer
 * must still be able to stop dunning). `no_subscription`, terminal
 * (`canceled`/`paused`/`unpaid`) and the existing `cancel_at_period_end: true`
 * state all hide it; the TrialStatusBanner owns the resume flow for the latter.
 */
function canShowCloseAccount(billing) {
	if (!billing) return false;
	if (billing.cancelAt) return false;
	return billing.status === 'active' || billing.status === 'trialing' || billing.status === 'past_due';
}

const SECTION_TABS = [
	{ id: 'plan', label: 'Plan' },
	{ id: 'billing', label: 'Billing' },
	{ id: 'invoices', label: 'Invoices' }
];

function SectionTabs({ value, onChange }) {
	return (
		<div className="flex flex-wrap gap-2">
			{SECTION_TABS.map((item) => {
				const active = value === item.id;
				return (
					<button
						key={item.id}
						type="button"
						onClick={() => onChange(item.id)}
						aria-pressed={active}
						className={cn(
							'inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition',
							active
								? 'border-foreground bg-foreground text-background'
								: 'border-border bg-accent/40 text-foreground hover:bg-accent'
						)}
					>
						{item.label}
					</button>
				);
			})}
		</div>
	);
}

function ErrorAlert({ message, onDismiss }) {
	return (
		<div className="flex items-start gap-3 rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-sm text-foreground">
			<div className="font-semibold">Couldn't complete that</div>
			<div className="flex-1">{message}</div>
			<button type="button" onClick={onDismiss} className="text-xs font-semibold text-muted-foreground hover:text-foreground">
				Dismiss
			</button>
		</div>
	);
}

function BillingSkeleton() {
	return (
		<div className="max-w-3xl space-y-8">
			<Skeleton className="h-4 w-32" />
			<Skeleton className="h-10 w-72" />
			<Skeleton className="h-20 w-full" />
			<SoftCard className="space-y-4 !p-5 md:!p-6">
				<Skeleton className="h-4 w-40" />
				<Skeleton className="h-24 w-full" />
			</SoftCard>
			<SoftCard className="space-y-4 !p-5 md:!p-6">
				<Skeleton className="h-4 w-40" />
				<Skeleton className="h-24 w-full" />
			</SoftCard>
		</div>
	);
}
